import React, { useEffect, createContext, useContext, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

const TAG_TO_PATH: Record<string, string> = {
  aws: '/sts/infra-provisioning/aws',
  azure: '/sts/infra-provisioning/azure',
  gcp: '/sts/infra-provisioning/gcp',
  'on-premises': '/sts/infra-provisioning/on-premises',
  'patch-management': '/sts/operations/patch-management',
  'change-management': '/sts/operations/change-management',
  linux: '/sts/operations/linux',
  windows: '/sts/operations/windows',
};

export interface TemplateContextInfo {
  derivedPath: string | null;
  tags: string[];
  loading: boolean;
}

const TemplateContext = createContext<TemplateContextInfo>({
  derivedPath: null,
  tags: [],
  loading: false,
});

export const TemplateContextProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const catalogApi = useApi(catalogApiRef);
  const [info, setInfo] = useState<TemplateContextInfo>({ derivedPath: null, tags: [], loading: false });
  const resolvedRef = useRef<string | null>(null);

  const agentMatch = location.pathname.match(/^\/sts\/agent-chat\/[^/]+\/([^/]+)/);
  const templateMatch = location.pathname.match(/^\/sts\/create\/templates\/([^/]+)\/([^/]+)/);
  const entityRef = agentMatch?.[1]
    ? `template:default/${agentMatch[1]}`
    : templateMatch
    ? `template:${templateMatch[1]}/${templateMatch[2]}`
    : null;

  useEffect(() => {
    if (!entityRef) {
      resolvedRef.current = null;
      setInfo({ derivedPath: null, tags: [], loading: false });
      return;
    }
    let cancelled = false;
    catalogApi
      .getEntityByRef(entityRef)
      .then(entity => {
        if (cancelled) return;
        const tags: string[] = entity?.metadata?.tags ?? [];
        const derivedPath = tags.map(t => TAG_TO_PATH[t]).find(Boolean) ?? null;
        resolvedRef.current = entityRef;
        setInfo({ derivedPath, tags, loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        resolvedRef.current = entityRef;
        setInfo({ derivedPath: null, tags: [], loading: false });
      });
    return () => { cancelled = true; };
  }, [entityRef, catalogApi]);

  // If entityRef changed but hasn't resolved yet, report loading
  const loading = Boolean(entityRef) && resolvedRef.current !== entityRef;
  const contextValue: TemplateContextInfo = { ...info, loading };

  return <TemplateContext.Provider value={contextValue}>{children}</TemplateContext.Provider>;
};

export function useTemplateContextInfo(): TemplateContextInfo {
  return useContext(TemplateContext);
}
